import { supabase } from '../lib/supabase';
import { validateUUIDOrThrow } from '../utils/uuidValidator';

/**
 * Client Authentication Service
 * Handles client signup, login, logout, and session management
 */
export const clientAuthService = {
  /**
   * Sign up a new client
   */
  async signUp(name, email, phone, password) {
    try {
      // Use server-side bcrypt hashing via Supabase RPC
      const { data: hashData, error: hashError } = await supabase?.rpc('hash_client_password', { password });
      
      let passwordHash;
      if (hashError || !hashData) {
        const encoder = new TextEncoder();
        const dataBytes = encoder?.encode(password);
        const hash = await crypto.subtle?.digest('SHA-256', dataBytes);
        passwordHash = btoa(String.fromCharCode(...new Uint8Array(hash)));
      } else {
        passwordHash = hashData;
      }

      // ── ÉTAPE 1 : Normaliser le téléphone (retirer espaces, garder +33668174836) ──
      const normalizedPhone = phone?.trim()?.replace(/\s+/g, '') || null;
      const normalizedEmail = email?.trim()?.toLowerCase() || null;

      // ── ÉTAPE 2 : Rechercher un client existant par email OU téléphone ──
      let existingClient = null;
      try {
        const orFilter = [
          normalizedEmail ? `email.eq.${normalizedEmail}` : null,
          normalizedPhone ? `phone.eq.${normalizedPhone}` : null
        ]?.filter(Boolean)?.join(',');

        if (orFilter) {
          const { data: found, error: searchError } = await supabase
            ?.from('clients')
            ?.select('*')
            ?.or(orFilter)
            ?.limit(1);

          if (searchError) {
            console.error('Erreur recherche client existant:', searchError);
          } else if (found && found?.length > 0) {
            existingClient = found?.[0];
          }
        }
      } catch (searchErr) {
        console.error('Erreur lors de la recherche client:', searchErr);
      }

      let clientRecord;

      if (existingClient) {
        // ── ÉTAPE 3 : Client existant → UPDATE uniquement, jamais INSERT ──
        console.log('Client existant trouvé, mise à jour du compte:', existingClient?.id);

        const updatePayload = {
          password_hash: passwordHash,
          updated_at: new Date()?.toISOString()
        };
        // Mettre à jour email si absent
        if (!existingClient?.email && normalizedEmail) updatePayload.email = normalizedEmail;
        // Mettre à jour name si absent
        if (!existingClient?.name && name) updatePayload.name = name;

        const { data: updatedClient, error: updateError } = await supabase
          ?.from('clients')
          ?.update(updatePayload)
          ?.eq('id', existingClient?.id)
          ?.select()
          ?.single();

        if (updateError) throw updateError;
        clientRecord = updatedClient;
      } else {
        // ── ÉTAPE 4 : Nouveau client → INSERT ──
        try {
          const { data: newClient, error: insertError } = await supabase
            ?.from('clients')
            ?.insert({
              name,
              email: normalizedEmail,
              phone: normalizedPhone,
              password_hash: passwordHash
            })
            ?.select()
            ?.single();

          if (insertError) {
            // ── ÉTAPE 5 : Rattraper l'erreur de contrainte unique (clients_phone_key) ──
            const isUniqueViolation =
              insertError?.code === '23505' || insertError?.message?.includes('unique') ||
              insertError?.message?.includes('duplicate') ||
              insertError?.message?.includes('clients_phone_key') ||
              insertError?.message?.includes('clients_email_key');

            if (isUniqueViolation) {
              console.warn('Contrainte unique détectée, recherche du client existant pour UPDATE...');

              // Rechercher à nouveau le client en conflit
              const orFilter2 = [
                normalizedEmail ? `email.eq.${normalizedEmail}` : null,
                normalizedPhone ? `phone.eq.${normalizedPhone}` : null
              ]?.filter(Boolean)?.join(',');

              const { data: conflictClients } = await supabase
                ?.from('clients')
                ?.select('*')
                ?.or(orFilter2)
                ?.limit(1);

              if (conflictClients && conflictClients?.length > 0) {
                const conflictClient = conflictClients?.[0];
                const { data: updatedConflict, error: updateConflictError } = await supabase
                  ?.from('clients')
                  ?.update({
                    password_hash: passwordHash,
                    email: normalizedEmail || conflictClient?.email,
                    name: name || conflictClient?.name,
                    updated_at: new Date()?.toISOString()
                  })
                  ?.eq('id', conflictClient?.id)
                  ?.select()
                  ?.single();

                if (updateConflictError) throw updateConflictError;
                clientRecord = updatedConflict;
              } else {
                throw insertError;
              }
            } else {
              throw insertError;
            }
          } else {
            clientRecord = newClient;
          }
        } catch (insertCatchError) {
          // Re-throw si ce n'est pas déjà géré
          if (clientRecord) {
            // déjà résolu dans le bloc unique violation
          } else {
            throw insertCatchError;
          }
        }
      }

      // Créer la session
      const sessionToken = this.generateToken();
      this.saveSession(sessionToken, clientRecord);

      return { client: clientRecord, sessionToken };
    } catch (error) {
      throw new Error(error.message || 'Erreur lors de la création du compte');
    }
  },

  /**
   * Sign in an existing client
   */
  async login(emailOrPhone, password) {
    try {
      // Normalize input: remove spaces and handle phone formats
      const normalizedInput = emailOrPhone?.trim();
      const normalizedPhone = normalizedInput?.replace(/\s/g, '');
      
      // Check if input looks like an email
      const isEmail = normalizedInput?.includes('@');
      
      // Build query that checks both email and normalized phone
      let query = supabase?.from('clients')?.select('*');
      
      if (isEmail) {
        query = query?.eq('email', normalizedInput?.toLowerCase());
      } else {
        // For phone: try exact match and also without spaces
        query = query?.or(`phone.eq.${normalizedPhone},phone.eq.${normalizedInput}`);
      }
      
      const { data: clients, error: fetchError } = await query?.limit(1);

      if (fetchError) {
        return { success: false, error: 'Erreur de connexion au serveur' };
      }
      
      if (!clients || clients?.length === 0) {
        return { success: false, error: 'Email ou mot de passe incorrect' };
      }

      const client = clients?.[0];

      // Try bcrypt verification via Supabase RPC first
      let passwordValid = false;
      
      try {
        const { data: isValid, error: verifyError } = await supabase?.rpc('verify_client_password', {
          input_password: password,
          stored_hash: client?.password_hash
        });

        if (!verifyError && isValid === true) {
          passwordValid = true;
        }
      } catch (rpcError) {
        // RPC call failed, will try SHA-256 fallback below
        console.warn('RPC verify_client_password failed, trying fallback:', rpcError);
      }

      // Fallback: try SHA-256 comparison for accounts created with old method
      if (!passwordValid) {
        try {
          const encoder = new TextEncoder();
          const dataBytes = encoder?.encode(password);
          const hash = await crypto.subtle?.digest('SHA-256', dataBytes);
          const sha256Hash = btoa(String.fromCharCode(...new Uint8Array(hash)));
          
          if (client?.password_hash === sha256Hash) {
            passwordValid = true;
          }
        } catch (sha256Error) {
          console.warn('SHA-256 fallback failed:', sha256Error);
        }
      }

      if (!passwordValid) {
        return { success: false, error: 'Email ou mot de passe incorrect' };
      }

      // Create session
      const sessionToken = this.generateToken();
      this.saveSession(sessionToken, client);
      
      return { success: true, client, sessionToken };
    } catch (error) {
      console.error('Client login error:', error);
      return { success: false, error: error?.message || 'Erreur de connexion' };
    }
  },

  /**
   * Sign out current client
   */
  async signOut() {
    try {
      const token = this.getSessionToken();
      if (token) {
        // Delete session from database
        await supabase?.from('client_sessions')?.delete()?.eq('session_token', token);
      }
      
      this.clearSession();
    } catch (error) {
      // Always clear local session even if deletion fails
      this.clearSession();
      throw new Error('Erreur lors de la déconnexion');
    }
  },

  /**
   * Get current authenticated client
   */
  async getCurrentClient() {
    const token = this.getSessionToken();
    if (!token) return null;

    try {
      const cachedClient = this.getCachedClient();
      if (!cachedClient?.id) {
        this.clearSession();
        return null;
      }

      console.log('Debug - clientAuthService.getCurrentClient - valeur de clientId:', cachedClient?.id);
      
      // ✅ VALIDATION UUID AVANT LA REQUÊTE
      validateUUIDOrThrow(cachedClient?.id, 'ID client');
      
      // Fetch client details
      const { data: client, error: fetchError } = await supabase?.from('clients')?.select('*')?.eq('id', cachedClient?.id)?.single();

      if (fetchError) {
        this.clearSession();
        return null;
      }

      return client;
    } catch (error) {
      this.clearSession();
      return null;
    }
  },

  /**
   * Update client loyalty points
   */
  async updateLoyaltyPoints(clientId, points) {
    console.log('Debug - clientAuthService.updateLoyaltyPoints - valeur de clientId:', clientId);
    
    // ✅ VALIDATION UUID AVANT LA REQUÊTE
    validateUUIDOrThrow(clientId, 'ID client');
    
    try {
      const { data, error } = await supabase?.from('clients')?.update({ loyalty_points_total: points })?.eq('id', clientId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error('Erreur lors de la mise à jour des points de fidélité');
    }
  },

  /**
   * Get client shipments
   */
  async getClientShipments(clientId) {
    console.log('Debug - clientAuthService.getClientShipments - valeur de clientId:', clientId);
    
    // ✅ VALIDATION UUID AVANT LA REQUÊTE
    validateUUIDOrThrow(clientId, 'ID client');
    
    try {
      // Query directly from shipments table without complex joins
      const { data, error } = await supabase?.from('shipments')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error loading shipments:', error);
      throw new Error('Erreur lors du chargement des envois');
    }
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(email) {
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-reset-password-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email?.trim()?.toLowerCase() })
      });

      if (!response?.ok) {
        // Still return success for security
        return { success: true, message: 'Si cet email existe, un lien de réinitialisation a été envoyé.' };
      }

      const data = await response?.json();
      return data;
    } catch (error) {
      // Return success even on error (security best practice)
      return { success: true, message: 'Si cet email existe, un lien de réinitialisation a été envoyé.' };
    }
  },

  // ==================== HELPER METHODS ====================

  /**
   * Generate random session token
   */
  generateToken() {
    return btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
  },

  /**
   * Hash password (client-side - for fallback only)
   */
  async hashPassword(password) {
    // This is a fallback - actual hashing should be done server-side
    const encoder = new TextEncoder();
    const data = encoder?.encode(password);
    const hash = await crypto.subtle?.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)));
  },

  /**
   * Save session to localStorage
   */
  saveSession(token, client) {
    localStorage.setItem('client_session_token', token);
    localStorage.setItem('client_data', JSON.stringify({
      id: client?.id,
      name: client?.name,
      email: client?.email,
      phone: client?.phone,
      loyaltyPointsTotal: client?.loyalty_points_total
    }));
  },

  /**
   * Get session token from localStorage
   */
  getSessionToken() {
    return localStorage.getItem('client_session_token');
  },

  /**
   * Get cached client data
   */
  getCachedClient() {
    const data = localStorage.getItem('client_data');
    return data ? JSON.parse(data) : null;
  },

  /**
   * Clear session from localStorage
   */
  clearSession() {
    localStorage.removeItem('client_session_token');
    localStorage.removeItem('client_data');
  },

  /**
   * Check if client is authenticated
   */
  isAuthenticated() {
    return !!this.getSessionToken();
  }
};

export default clientAuthService;